import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaPlus, FaEye, FaEyeSlash, FaTools } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const EMPTY_FORM = { title: "", subject: "", class: "", topic: "", body: "" };

const TeacherLessonManager = () => {
    const navigate = useNavigate();
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const fetchLessons = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/mine`, { headers: authHeaders() });
            const data = await res.json();
            setLessons(data.data || []);
        } catch (err) {
            toast.error("Could not load lessons");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLessons(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to create lesson");
            toast.success("Lesson created as a draft");
            setForm(EMPTY_FORM);
            setShowForm(false);
            fetchLessons();
            navigate(`/teacher/lessons/${data.data.id}/build`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (lesson) => {
        const action = lesson.status === "published" ? "unpublish" : "publish";
        try {
            const res = await fetch(`${API_BASE_URL}/api/lessons/${lesson.id}/${action}`, {
                method: "POST",
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`Failed to ${action}`);
            toast.success(`Lesson ${action}ed`);
            fetchLessons();
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white">
                        <FaArrowLeft size={18} />
                    </button>
                    <h1 className="text-xl font-bold text-white">Lesson Manager</h1>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-white text-blue600 px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
                    <FaPlus /> New Lesson
                </button>
            </nav>

            <div className="max-w-4xl mx-auto p-6">
                {showForm && (
                    <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm p-6 space-y-4 mb-6">
                        <input required placeholder="Lesson title" className="border rounded-lg p-2 w-full" value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        <div className="grid grid-cols-3 gap-4">
                            <input required placeholder="Subject" className="border rounded-lg p-2" value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                            <input required placeholder="Class (e.g. SS2)" className="border rounded-lg p-2" value={form.class}
                                onChange={(e) => setForm({ ...form, class: e.target.value })} />
                            <input required placeholder="Topic" className="border rounded-lg p-2" value={form.topic}
                                onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                        </div>
                        <textarea required placeholder="Lesson note body" className="border rounded-lg p-2 w-full" rows={6} value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })} />
                        <button disabled={saving} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                            {saving ? "Saving..." : "Save Draft"}
                        </button>
                    </form>
                )}

                {loading && <p className="text-gray-500">Loading...</p>}
                {!loading && lessons.length === 0 && <p className="text-gray-500">No lessons yet — create your first one.</p>}

                <div className="space-y-3">
                    {lessons.map((lesson) => (
                        <div key={lesson.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-800">{lesson.title}</h3>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${lesson.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                        {lesson.status}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500">{lesson.subject} · {lesson.class} · {lesson.topic}</p>
                                <p className="text-xs text-gray-400 mt-1">{lesson.openedCount} opened · {lesson.completedCount} completed</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/teacher/lessons/${lesson.id}/build`)} className="text-blue600 hover:bg-blue-50 p-2 rounded-lg" title="Build with AI">
                                    <FaTools />
                                </button>
                                <button onClick={() => toggleStatus(lesson)} className="text-gray-600 hover:bg-gray-100 p-2 rounded-lg"
                                    title={lesson.status === "published" ? "Unpublish" : "Publish"}>
                                    {lesson.status === "published" ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TeacherLessonManager;
